import validator from 'validator';
import { isPossiblePhoneNumber } from 'libphonenumber-js';

import LocationObject from 'entities/locationObject/model';

import { validateLocationObject } from './validateLocationObject';

export const required =
  () =>
  (value?: string | number): string | undefined =>
    value || typeof value === 'number' ? undefined : 'Required';

export const requiredRadioButton = (props: any): string | undefined =>
  props.active ? undefined : 'Required';

export const isAddress =
  () =>
  (value?: string): string | undefined =>
    !value || /(.+, .+, .*)/i.test(value) ? undefined : 'Please input a full mailing address';

export const isLocationObject =
  () =>
  (value?: LocationObject): string | undefined => {
    try {
      if (!value) {
        return undefined;
      }
      validateLocationObject(value);
      return undefined;
    } catch (e) {
      return 'Please input a full mailing address';
    }
  };

export const isUSLocationObject =
  () =>
  (value?: LocationObject): string | undefined =>
    !value || value.countryCode === 'US' ? undefined : 'Please input a US mailing address';

export const isHexColor =
  () =>
  (value?: string): string | undefined =>
    !value || /^#[0-9a-f]{6}$/gi.test(value)
      ? undefined
      : `${value} is not a valid color code (please use a hex code eg. #ffffff)`;

export const isURL =
  () =>
  (value?: any): string | undefined =>
    typeof value === 'string' && value.length > 0 && !validator.isURL(value)
      ? "That doesn't look like a URL"
      : undefined;

const supportedVideoPlatforms = ['youtube', 'youtu.be', 'vimeo', 'brightcove'];
export const isSupportedVideoLink =
  () =>
  (value?: any): string | undefined =>
    typeof value === 'string' &&
    value.length > 0 &&
    !supportedVideoPlatforms.some((platform) => value.includes(platform))
      ? 'We only support youtube, vimeo and brightcove links'
      : undefined;

export const isEmail =
  () =>
  (value?: string): string | undefined =>
    !value || validator.isEmail(value) ? undefined : "That doesn't look like an email address";

export const isNumber =
  () =>
  (value?: string | number): string | undefined =>
    Number.isFinite(Number(value)) ? undefined : 'Must be a number';

export const minLength =
  (length: number) =>
  (value?: string | any): string | undefined =>
    (value && (value.size || value.length) >= length) || !value
      ? undefined
      : `Must be at least ${length} characters long.`;

export const maxLength =
  (length: number) =>
  (value?: string | any): string | undefined =>
    value && (value.size || value.length) > length
      ? `Must be ${length} ${typeof value === 'string' ? 'character' : 'item'}${
          length !== 1 ? 's' : ''
        } or less`
      : undefined;

export const maxLengthRichText =
  (length = 5000) =>
  (value: unknown) =>
    typeof value === 'string' && value?.replace(/<[^>]+>/g, '')?.trim()?.length > length
      ? `Must be less than ${length} characters`
      : undefined;

export const isPhone =
  () =>
  (value?: string): string | undefined => {
    if (!value) {
      return undefined;
    }
    let phoneNumber = value?.replace(/[^0-9+]/g, '');
    if (!phoneNumber.startsWith('+')) {
      phoneNumber = `+${phoneNumber}`;
    }
    return isPossiblePhoneNumber(phoneNumber) ? undefined : 'Must enter valid phone number';
  };

export const notEmpty =
  () =>
  (value?: any): string | undefined =>
    !value || value.length === 0 || value.size === 0 ? 'Required' : undefined;

export const isEin =
  () =>
  (value?: string): string | undefined =>
    !value || /^[0-9]\d?-?\d{7}$/i.test(value)
      ? undefined
      : 'Must be a valid Employer Identification Number (EIN)';

export const isCompanyEmail =
  (companyEmailDomains: string[]) =>
  (value?: string): string | undefined =>
    !value ||
    companyEmailDomains.length === 0 ||
    companyEmailDomains.some((domain) => value.endsWith(`@${domain}`))
      ? undefined
      : `Email address must be among: ${companyEmailDomains.join(', ')}`;
